import * as React from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Checkbox,
  FormControlLabel,
  Stack,
  Box,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import i18n from "../../utilities/i18n";
import CommonButton from "../commons/Buttons";

export default function CustomizedSmallDialogs(props) {
  const {
    showModal,
    setShowModalWindow,
    type,
    header,
    subHeader,
    children,
    fullWidth = true,
    maxWidth = "sm",
    minHeight,
    onHide,
    resetForm
  } = props;

  const flags = {
    isDelete: type === "delete",
    isSave: type === "save",
    isSubmit: type === "formSubmit",
    isAlert: type === "alert",
    isReset: type === "reset",
    isSendMail: type === "sendMail",
    isYes: type === "yes",
    isAdd: type === "add",
    isExport: type === "export",
    isAssign: type === "assign",
    isConfirmation: type === "confirmation",
    isCancel: type === "cancel_confirm",
    isComplete: type === "complete",
    isUpdate: type === "update",
    isSubmitResponse: type === "review_respond",
    isHold: type === "hold",
  };

  const isNoSaveCancel = props.isNoSaveCancel === true ? true : false;
  const saveClose = Object.prototype.hasOwnProperty.call(props, "saveType") ? props.saveType ? true : false : false;

  const cropImageBtn = props.cropImageBtn ? props.cropImageBtn : false;

  const handleClose = () => {
    if (setShowModalWindow) {
      setShowModalWindow(false);
    }
    if (resetForm) resetForm();
    if (onHide) onHide();
  };

   // standard MUI breakpoints
   const standardSizes = ["xs", "sm", "md", "lg", "xl"];
   // Check if the passed maxWidth is a standard size or a custom string (eg: "500px")
   const isStandardSize = standardSizes.includes(maxWidth);

  return (
    <Dialog
      open={showModal}
      onClose={(_, reason) => reason !== "backdropClick" && handleClose()}
      fullWidth={fullWidth}
      maxWidth={isStandardSize ? maxWidth : false}
      aria-labelledby="modal-title"
      PaperProps={{
        sx: {
          // If it's a custom size like '500px', apply it here
          ...(!isStandardSize && { maxWidth: maxWidth, width: '100%' }),
          // Also applying your minHeight prop since it wasn't being used!
          ...(minHeight && { minHeight: minHeight }) 
        }
      }}
    >
    <DialogTitle id="modal-title">
      <Box display="flex" flexDirection="column">
        <Box display="flex" alignItems="center" gap={2}>
          {header}
          {props.eligibility && (
            <CommonButton 
              size="small" 
              onClick={props.update} 
              label={i18n.t("buttons.update")} 
            />
          )}
        </Box>

        {subHeader && (
          <Box
            sx={{
              fontSize: "14px",
              color: "text.secondary",
              marginTop: "4px",
              fontWeight: 400
            }}
          >
            {subHeader}
          </Box>
        )}
      </Box>

        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 12,
            top: 10,
            zIndex: 1
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {!cropImageBtn && (
      <DialogContent 
        dividers 
        sx={{ minHeight: minHeight }}
        className={props.addNoOverflowXClass ? "overflow-x-hidden small" : "small"}
      >
        {children}
      </DialogContent>)}
      {cropImageBtn && (
        <DialogContent 
        dividers 
        sx={{ minHeight: minHeight }}
       className="cropImageModalBody"
      >
        {children}
      </DialogContent>)}
      {!isNoSaveCancel && (
        <DialogActions>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
            {flags.isExport && (
               <FormControlLabel
               control={
                 <Checkbox
                   checked={!!props.fieldsChecked}
                   onChange={props.onCheckBoxChange}
                   size="small"
                 />
               }
               label={i18n.t("buttons.selectAll")}
             />
            )}

            {props.chkInactive && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={props.inactive}
                    onChange={(e) => props.setInactive(e.target.checked)}
                    size="small"
                  />
                }
                label="Inactive"
              />
            )}
          </Box>
          <Stack direction="row" spacing={1.5}>
            {props.onDownload && (
                <Button
                  variant="contained"
                  component="a"
                  onClick={props.onDownload}
                >
                  Download
                </Button>
            )}
            {!flags.isAlert && !saveClose && !flags.isConfirmation && (
              <CommonButton label={i18n.t("buttons.cancel")} variant="outlined" onClick={handleClose} />
            )}
            
            {!flags.isAlert && saveClose && (
              <CommonButton label={i18n.t("buttons.no")} variant="outlined" onClick={handleClose} />
            )}

            {flags.isConfirmation && (
              <CommonButton label={i18n.t("buttons.no")} variant="outlined" onClick={props.goBack} />
            )}

            {flags.isHold && (
              <CommonButton label={props.saveLabel || i18n.t("buttons.save")} variant="contained"
                onClick={props.onSubmitHold} disabled={props.isHoldDisabled}
              />
            )}

            {flags.isAlert && (
              <CommonButton label={i18n.t("buttons.ok")} variant="contained" onClick={props.alertOK} />
            )}

            {flags.isAssign && (
              <CommonButton label={i18n.t("buttons.assign")} variant="contained" onClick={props.addAssigned} />
            )}

            {flags.isAdd && (
              <CommonButton label={i18n.t("buttons.add")} variant="contained" onClick={props.add} />
            )}

            {flags.isExport && (
              <CommonButton label={i18n.t("buttons.export")} variant="contained" onClick={props.exportData} />
            )}
            {flags.isComplete && (
              <CommonButton label={i18n.t("buttons.complete_work")} variant="contained" onClick={props.completeClaim} />
            )}
            {flags.isUpdate && (
              <CommonButton label={i18n.t("buttons.update")} variant="contained" onClick={props.update} />
            )}

            {flags.isDelete && (
              <CommonButton label={i18n.t("buttons.delete")} variant="contained" onClick={props.deleteItem} />
            )}

            {flags.isCancel && (
              <CommonButton label={i18n.t("buttons.cancel")} variant="contained" onClick={props.onCancel} />
            )}

            {flags.isConfirmation && (
              <CommonButton label={i18n.t("buttons.yes")} variant="contained" onClick={props.alertOK} />
            )}

            {flags.isSubmitResponse && (
              <CommonButton label={i18n.t("buttons.submit_response")} variant="contained" onClick={props.alertOK} />
            )}

            {props.selectNPIBtn && (
               <Button form="form_searchNPI" type="submit" variant="contained">
                {i18n.t("userPages.practices.labelSelectNPI")}
               </Button>
            )}

            {props.cropImageBtn && (
              <Button form="form_cropImage"  onClick={props.onCropImage} variant="contained" >
                {i18n.t("buttons.crop")}
              </Button>
            )}

            {flags.isSave && !props.hideSave && props.submit !== false && (
              <Button
                form="form_dataEntry"
                variant="contained"
                onClick={props.onSaveData}
                data-testid="modalSaveButton"
                type="submit"
              >
                {i18n.t("buttons.save")}
              </Button>
            )}

            {flags.isSave && !props.hideSave && props.submit == false && (
              <Button
                onClick={props.onSaveData}
                variant="contained"
              >
                {i18n.t("buttons.save")}
              </Button>
            )}

            {flags.isSubmit && (
              <Button form="formSubmit"  variant="contained" onClick={props.onSaveFormData}>
                {i18n.t("buttons.save")}
              </Button>
            )}

            {flags.isReset && (
              <Button form="formSubmit" onClick={props.onSaveFormData} variant="contained">
                {i18n.t("buttons.reset")}
              </Button>
            )}

            {flags.isSendMail && (
              <CommonButton onClick={props.onSaveFormData} label={i18n.t("buttons.sendMail")} variant="contained" />
            )}

            {flags.isYes && (
              <CommonButton onClick={props.inactveItem} label={i18n.t("buttons.yes")} variant="contained" />
            )}
          </Stack>
        </DialogActions>
      )}
    </Dialog>
  );
}

CustomizedSmallDialogs.propTypes = {
  showModal: PropTypes.bool,
  setShowModalWindow: PropTypes.func.isRequired,
  header: PropTypes.string,
  subHeader: PropTypes.string,
  type: PropTypes.string,
  children: PropTypes.node,
  onHide: PropTypes.func,
  resetForm: PropTypes.func,
};